import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import fr from 'facet-rest';

import metaOverrideSetMap from 'Resources';

const defaultValue = {
  items: [], // { key, title, collection }
};

const updateSearchParams = (history, items) => {
  const mappedItems = items.map((v) => {
    const { collection, metaOverrideName, autoSave } = v;
    const [resource] = collection;

    if (!resource.link('self')) {
      return null;
    }

    const res = {
      key: v.key,
      title: v.title,
      url: resource.link('self').url(),
      saved: v.saved,
    };

    if (metaOverrideName) {
      res.metaOverrideName = metaOverrideName;
    }
    if (autoSave) {
      res.autoSave = autoSave;
    }

    return res;
  }).filter(v => !!v); // filter out all entries that don't have a self link

  const url = new URL(window.location.href);

  if (mappedItems.length === 0) {
    url.searchParams.delete('dockedItems');
  } else {
    // NOTE: btoa() is deprecated, using the Node Buffer class instead.
    // See: https://nodejs.org/api/buffer.html#static-method-bufferfromstring-encoding
    const mappedItemsEncoded = Buffer.from(JSON.stringify(mappedItems)).toString('base64');
    url.searchParams.set('dockedItems', mappedItemsEncoded);
  }

  history.replace({
    pathname: url.pathname,
    search: url.search,
  });
};

const getMetaOverrideSet = (metaOverrideName) => {
  if (metaOverrideName) {
    const metaOverrideSet = metaOverrideSetMap[metaOverrideName];
    if (!metaOverrideSet) {
      throw new Error(`Missing meta override set in /Resources/index.js (${metaOverrideName})`);
    }

    return metaOverrideSet;
  }

  return null;
};

export const DockedContext = createContext(defaultValue);

export function DockedContextProvider({
  children,
}) {
  const [items, setItems] = useState(defaultValue.items);
  const history = useHistory();

  const saveItemState = (itemKey, stateKey, stateValue) => {
    const updatedItemState = [...items];
    const itemToUpdate = updatedItemState.find(item => item.key === itemKey);
    itemToUpdate.itemState = {
      ...itemToUpdate.itemState,
      [stateKey]: stateValue,
    };
    setItems(updatedItemState);
  };

  // Rename to dockItemWithCollection
  const dockItem = (key, title, collection, args = {}) => {
    const {
      metaOverrideName,
      autoSave,
      saved,
      displaySavedState = true,
    } = args;

    // Should only have a single collection
    if (collection?.resources().length() !== 1) {
      throw new Error('Collection must have exactly 1 resource for docked items.');
    }
    let coll = collection;

    const metaOverrideSet = getMetaOverrideSet(metaOverrideName);

    if (metaOverrideSet) {
      coll = collection.overrideMeta(metaOverrideSet);
    }

    // NOTE: This may only ever have serializable items in it, since they must be able to go into a query string.
    const item = {
      key,
      title,
      collection: coll,
      metaOverrideName,
      autoSave,
      saved,
      displaySavedState,
    };

    // Note: This is here to stop develoeprs from adding functions to the item state since they can't be persisted in the URL
    Object.entries(item).forEach(([k, v]) => {
      switch (typeof v) {
        case 'function':
          throw new Error(`Invalid value "${k}" of type "${typeof v}".`);
        /* no default */
      }
    });
    const updatedItems = [...items.filter(v => v.key !== key), item];
    updateSearchParams(history, updatedItems);
    setItems(updatedItems);
  };

  const setSaved = (key, saved) => {
    const item = items.find(v => v.key === key);
    item.saved = saved;
    const updatedItems = [...items.filter(v => v.key !== key), item];
    setItems(updatedItems);
  };

  const getItemTitle = (key) => {
    let savedSection = '';
    const item = items.find(v => v.key === key);
    if (item.displaySavedState) {
      savedSection = item.saved ? '- (Saved)' : '- (Unsaved)';
    }
    return `${item.title} ${savedSection}`;
  };

  const getItem = key => items.filter(v => v.key === key)[0];

  const undockItem = async (key, args = {}) => {
    const {
      skipSave = false,
    } = args;

    // See if the item exists
    const item = getItem(key);

    // If it does, save it unless explicitly told not to
    if (item && !skipSave) {
      const { collection: coll } = item;
      const [resource] = coll;

      try {
        await resource.submit();
      } catch (err) {
        // In the event of a failure during submission, return the errors so they can be dealt with
        // The default close in DockedItem will pop up a confirmation box with asking if an unsaved close is OK.
        return err;
      }
    }

    // Clear the URL bar of this item
    const updatedItems = [...items.filter(v => v.key !== key)];
    updateSearchParams(history, updatedItems);
    setItems(updatedItems);

    return null;
  };

  const isItemDocked = key => items.filter(v => v.key === key).length > 0;

  // eslint-disable-next-line arrow-body-style
  const loadDecodedItems = async (decodedItems) => {
    const loadedItems = await Promise.all(decodedItems.map(item => (
      fr.http.Get(item.url)
        .then(res => (fr.collection(res, getMetaOverrideSet(item.metaOverrideName))))
        .then(coll => ({
          key: item.key,
          title: item.title,
          metaOverrideName: item.metaOverrideName,
          collection: coll,
          autoSave: item.autoSave,
          saved: item.saved,
          displaySavedState: item.displaySavedState,
        }))
    )));

    setItems(loadedItems);
  };

  useEffect(() => {
    const url = new URL(window.location.href);
    const param = url.searchParams.get('dockedItems');

    if (param !== null) {
      loadDecodedItems(JSON.parse(atob(param)));
    }

    // Clear search params on dismount
    return () => updateSearchParams(history, []);
  }, []);

  return (
    <DockedContext.Provider
      value={{
        items,
        getItem,
        dockItem,
        isItemDocked,
        undockItem,
        setSaved,
        getItemTitle,
        saveItemState,
      }}
    >
      {children}
    </DockedContext.Provider>
  );
}

DockedContextProvider.propTypes = {
  children: PropTypes.any.isRequired,
};

export default DockedContextProvider;

export function useDockedContext() {
  const context = useContext(DockedContext);

  if (!context) throw new Error('useDockedContext must be called from a DockedContext.Provider');

  return context;
}
